import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { formatAttribute, formatValue } from '@/modules/ActivityLog/LogList/LogItem/Changeset/common';

import { sortByFieldName } from './sortByFieldName';

export const SEARCH_CHANGESET_CHANGES_KEY = 'SEARCH_CHANGESET_CHANGES_KEY';

interface SortPayload {
  empty?: boolean;
  unsorted?: boolean;
  sorted?: boolean;
}

interface PageablePayload {
  offset?: number;
  sort?: SortPayload;
  unpaged?: boolean;
  pageNumber?: number;
  pageSize?: number;
  paged?: boolean;
}

export interface ChangePayload {
  id?: number;
  field_name?: string;
  old_value?: string;
  new_value?: string;
  employee_id?: number;
  plan_id?: number;
  employee_name?: string;
  plan_name?: string;
}

interface ChangesetChangesResponsePayload {
  totalPages?: number;
  totalElements?: number;
  size?: number;
  content?: ChangePayload[];
  number?: number;
  sort?: SortPayload;
  pageable?: PageablePayload;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  empty?: boolean;
}

export interface Change {
  id?: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  employeeId?: number;
  planId?: number;
  employeeName?: string;
  planName?: string;
  uniqueGroupId?: string;
}

interface ChangesetChangesResponse {
  totalPages?: number;
  totalElements?: number;
  size?: number;
  content?: Change[];
  number?: number;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  empty?: boolean;
}

interface QueryParams {
  fieldNames?: string[];
  oldValue?: string;
  newValue?: string;
  page?: number;
  size?: number;
  sort?: string[];
  search?: string;
}

export const useSearchChangesetChangesQuery = (
  changesetId?: string,
  enabled?: boolean,
  params?: QueryParams
) => {
  const { data, isLoading, refetch } = useQuery(
    [SEARCH_CHANGESET_CHANGES_KEY, changesetId, enabled, params],
    () =>
      // TODO: this type is missing
      api.get<ChangesetChangesResponsePayload>(
        API_URL.AUDIT_CHANGESETS_SEARCH_BY_CHANGESET_ID(changesetId || ''),
        {
          params: {
            ...(params?.fieldNames ? { field_names: params.fieldNames } : {}),
            ...(params?.oldValue ? { old_value: params.oldValue } : {}),
            ...(params?.newValue ? { new_value: params.newValue } : {}),
            ...(params?.size ? { size: params.size } : {}),
            ...(params?.sort ? { sort: params.sort } : {}),
            ...(params?.page ? { page: params.page } : {}),
            ...(params?.search ? { search_value: params.search } : {}),
          },
        }
      ),
    {
      enabled: Boolean(changesetId && enabled),
    }
  );

  const formattedData = useMemo<ChangesetChangesResponse>(() => {
    const payload = data?.data;

    return {
      totalPages: payload?.totalPages,
      totalElements: payload?.totalElements,
      size: payload?.size,
      content: payload?.content?.sort(sortByFieldName)?.map((item) => ({
        id: toString(item?.id),
        ...(item?.field_name
          ? {
              fieldName: formatAttribute(item.field_name),
            }
          : {}),
        ...(item?.field_name && item?.old_value
          ? {
              oldValue: formatValue(item.field_name, item.old_value),
            }
          : {}),
        ...(item?.field_name && item?.new_value
          ? {
              newValue: formatValue(item.field_name, item.new_value),
            }
          : {}),
        employeeId: item?.employee_id,
        planId: item?.plan_id,
        employeeName: item?.employee_name,
        planName: item?.plan_name,
        uniqueGroupId: `employeeId_${item?.employee_id}_planName_${item?.plan_name}`,
      })),
      number: payload?.number,
      first: payload?.first,
      last: payload?.last,
      numberOfElements: payload?.numberOfElements,
      empty: payload?.empty,
    };
  }, [data]);

  return {
    formattedData,
    isLoading,
    refetch,
  };
};
